/**
 * The public chrome: the masthead, the footer, and the frame all three
 * signed-out pages render inside.
 *
 * ── WHY THIS WAS REBUILT ──────────────────────────────────────────────────
 * Thomas's review of the previous pass:
 *
 *   "This is essentially just a white page right now; with a hero section we
 *    brought in from outside. No watermarks, photos, branding elements. The
 *    logo on top is invisible (white on white), the start subscription button
 *    on top diverges from our brand style not to mention the whole top
 *    navigation. This looks like a wireframe not a finalized high fidelity
 *    EdTech subscription page for universities."
 *
 * Three separate faults, and one structural move fixes all three.
 *
 * 1. THE LOGO WAS INVISIBLE. The header loaded the P3 lockup from the Webflow
 *    CDN. That asset is drawn `fill="#fff"` throughout because its home is the
 *    maroon nav on the marketing site, so on a white header it rendered as
 *    nothing, and `filter: brightness(0)` "fixed" it by flattening a
 *    three-colour lockup into a black silhouette. Both logos now ship from
 *    this repo and each is used on the ground it was drawn for: the white SVG
 *    on the dark masthead, the colour PNG in the footer under the documented
 *    `brightness(0) invert(1)` dark-ground recipe, which also gives the print
 *    sheet the colour lockup for free by simply dropping the filter.
 *
 * 2. THE HERO READ AS PASTED IN, because it was an island: a white nav above
 *    it, a white body below it, margin on all four sides. THE FIX IS THAT THE
 *    NAV AND THE HERO ARE NOW ONE CONTINUOUS DARK SURFACE. The gradient starts
 *    at the very top of the viewport, carries the nav, and flows into the hero
 *    with no seam, exactly the way www.pulseofp3.org's own masthead does. That
 *    single change also removes the largest white expanse on the page and puts
 *    a white logo on a dark ground, which is fault 1 again.
 *
 *    Mechanically the gradient is painted by ONE element, `.eg-mast-bg` inside
 *    GatewayHero, which bleeds full width and reaches UP past the header. One
 *    element means the gradient cannot mis-register across the nav/hero
 *    boundary, which two matched backgrounds would eventually do. The header
 *    is transparent over it and says so with `tone="masthead"`.
 *
 * 3. THE NAV DID NOT LOOK LIKE P3. Every value below is lifted from the live
 *    site's `.p3-nav` (JS Files - Live Pages/pp-combined.js and
 *    terms-combined.js): 36px logo, links at Inter/Jakarta 14px 500 in
 *    rgba(255,255,255,.85) with 32px gaps, CTA pill at 10px 24px with a 50px
 *    radius and weight 600. A visitor arriving from pulseofp3.org sees the bar
 *    they just left. The one deliberate divergence is the container: the live
 *    site is full-bleed with 40px side padding because its hero is too, while
 *    this page's hero sits in the 1120px shell, so the masthead uses the shell
 *    and the logo lines up with the H1 beneath it. A nav whose logo misses the
 *    headline by 140px on a surface they visibly share looks broken.
 *
 * ── WHY THE HEADER IS STICKY AND THE FOOTER IS NOT ────────────────────────
 * The route into a subscription has to be reachable from anywhere on a long
 * page, so the bar stays and condenses and frosts once it leaves the masthead
 * (the same move p3-web's admin shell makes on scroll). Everything else on the
 * page is reading material. The footer carries the supplier identity a
 * procurement office copies into a requisition, which is something they scroll
 * to on purpose.
 *
 * ── WHERE THE CSS LIVES ───────────────────────────────────────────────────
 * src/app/styles/gateway.css sections 1 (masthead) and 12 (footer), imported
 * once from main.tsx.
 */

import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";

import { cn } from "~/app/lib/cn";
import { LEGAL } from "~/app/lib/pricing";

/**
 * The white lockup, for dark grounds only. Self-hosted: the CDN copy is the
 * same artwork, but a masthead that fails when a Webflow asset URL is rotated
 * is a masthead that fails silently.
 */
export const P3_LOGO_WHITE = "/brand/p3-logo-white.svg";

/**
 * The colour lockup: deep maroon, dark red and crimson on transparent. Correct
 * unfiltered on any light ground, which is what makes it the right asset for
 * the footer, where print needs a visible mark on white paper.
 */
export const P3_LOGO_COLOR = "/brand/p3-logo-horizontal.png";

/** 946.6 x 292.6 in the file. At 36px tall that is 116.5px wide. */
const LOGO_W = 117;
const LOGO_H = 36;

export type GatewayPage = "partner" | "pricing" | "trust" | "checkout" | "login";

/**
 * "masthead" means a dark gradient surface is painted behind this header by
 * the hero below it, so the bar itself is transparent until it is scrolled.
 * "solid" paints its own dark bar and is the default, because /trust and
 * /checkout render light heroes and a transparent bar there would put the
 * white lockup back on cream, which is the bug this pass exists to fix.
 */
export type HeaderTone = "solid" | "masthead";

export interface GatewayHeaderProps {
  current: GatewayPage;
  tone?: HeaderTone;
}

interface NavItem {
  readonly href: string;
  readonly label: string;
  readonly page?: GatewayPage;
  /** True when the destination is another origin. */
  readonly external?: boolean;
}

/**
 * The marketing site. The lockup top left goes here rather than to /pricing:
 * a visitor who clicks a logo expects the organization's front door, and this
 * host is one page of P3, not P3. It LEAVES THE APP, so it is a plain anchor
 * and the router leaves it alone.
 */
export const HOMEPAGE_URL = "https://www.pulseofp3.org";

/** Where an institution's administrator signs in: this host's own screen,
    since Sep 9 2026 (pages/Login.tsx). A work email, the emailed code,
    then /admin. */
export const LOGIN_PATH = "/login";

/** The student platform. Separate origin, separate app: the sign-in screen
    points a student who lands here at it. */
export const PLATFORM_URL = "https://platform.pulseofp3.org";

/** Where the institutional story lives on the marketing site. First in the
    nav because it is the page a visitor most likely came from, and the one
    that explains what any of this is before a price is on screen. */
export const PARTNER_URL = "https://www.pulseofp3.org/partner";

export const GATEWAY_NAV: readonly NavItem[] = [
  { href: "/overview", label: "Overview", page: "partner" },
  { href: "/pricing", label: "Pricing", page: "pricing" },
  /* "Security", not "Trust". Procurement asks for a security review, not a
     trust review, and the page answers the former. Renamed Sep 8 2026. */
  { href: "/security", label: "Security", page: "trust" },
  /* Login stays on this host (Sep 9 2026). It used to leave for the
     student platform, the only sign-in that existed; an institution's
     administrator now signs in here and lands on /admin. The screen itself
     points a student at the app. */
  { href: LOGIN_PATH, label: "Login", page: "login" },
];

const CTA_LABEL = "Register";

/** The bar takes its scrolled ground past this, and gives it up below SCROLL_OFF. */
const SCROLL_ON = 50;
const SCROLL_OFF = 40;

/* ==========================================================================
   Header
   ========================================================================== */

export function GatewayHeader({ current, tone = "solid" }: GatewayHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const drawerId = useId();
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const drawerRef = useRef<HTMLDivElement | null>(null);

  /* The frosted state. Read through rAF because a scroll handler that calls
     setState on every event fires several hundred times a second on a
     trackpad and the bar has exactly two states.

     THE THRESHOLD IS THE LIVE SITE'S, 50px (hp2-combined.js:
     `nav.classList.toggle('scrolled', window.scrollY > 50)`). Ours flipped
     at 8px, inside the band a phone's bounce and its collapsing toolbar
     move through, so the bar flickered between its two grounds on every
     wobble (Thomas, Sep 9 2026 night: "jittery when I scroll up/down,
     especially on mobile"). The way back is 10px lower than the way up, so
     a single wobble never crosses the same line twice. */
  useEffect(() => {
    let frame = 0;
    const read = () => {
      frame = 0;
      const y = window.scrollY;
      setScrolled((was) => (y > SCROLL_ON ? true : y < SCROLL_OFF ? false : was));
    };
    const onScroll = () => {
      if (frame === 0) frame = window.requestAnimationFrame(read);
    };
    read();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, []);

  /* While the drawer is open: the page underneath does not scroll, and Escape
     closes it and hands focus back to the control that opened it. */
  useEffect(() => {
    if (!open) return;
    const body = document.body;
    const previous = body.style.overflow;
    body.style.overflow = "hidden";
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      toggleRef.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      body.style.overflow = previous;
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  /* Focus enters the drawer on open. Without this the next Tab lands on
     whatever followed the toggle in the document, which is the page behind a
     full-screen overlay. It lands on the DIALOG ITSELF, not on its first
     control: iOS Safari paints a focus ring on a button focused by script
     after a tap, so a clean refresh and one tap showed "Close" outlined
     (Thomas, Sep 9 2026). The dialog carries tabIndex -1 and no ring; the
     next Tab reaches the first link. */
  useEffect(() => {
    if (!open) return;
    drawerRef.current?.focus({ preventScroll: true });
  }, [open]);

  function trapTab(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.key !== "Tab") return;
    const nodes = drawerRef.current?.querySelectorAll<HTMLElement>(
      "a[href], button:not([disabled])",
    );
    if (!nodes || nodes.length === 0) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  return (
    <header
      className={cn("eg-top no-print", tone === "masthead" && "is-masthead")}
      data-scrolled={scrolled ? "true" : "false"}
    >
      {/* The visible bar is this absolutely positioned child, not the header
          itself. `.eg-top` reserves a fixed slice of flow that never changes,
          so the bar can condense on scroll without shortening the document
          and pulling everything below it up by the difference. */}
      <div className="eg-top-bar">
        {/* NO `.eg-shell` HERE, DELIBERATELY. The shell is a 1120px centred
            container, and the live homepage nav is full bleed with 40px of
            side padding, so its lockup sits at x=40 from the viewport. Wrapping
            the bar in the shell put ours at x=160 on a wide monitor, which is
            the "logo placement is not the same" Thomas reported. */}
        <div className="eg-top-in">
            <a className="eg-mark" href={HOMEPAGE_URL}>
              <img
                className="eg-mark-img"
                src={P3_LOGO_WHITE}
                alt="P3"
                width={LOGO_W}
                height={LOGO_H}
              />
              <span className="eg-mark-rule" aria-hidden="true" />
              <span className="eg-mark-word">Enterprise</span>
            </a>

            <nav className="eg-top-nav" aria-label="Gateway">
              {GATEWAY_NAV.map((item) => (
                <a
                  className="eg-top-link"
                  key={item.href}
                  href={item.href}
                  aria-current={
                    item.page && item.page === current ? "page" : undefined
                  }
                >
                  {item.label}
                </a>
              ))}
              <a
                className="eg-nav-cta"
                href="/checkout"
                aria-current={current === "checkout" ? "page" : undefined}
              >
                {CTA_LABEL}
              </a>
            </nav>

            <button
              ref={toggleRef}
              type="button"
              className="eg-burger"
              aria-expanded={open}
              aria-controls={drawerId}
              aria-label={open ? "Close menu" : "Menu"}
              onClick={() => setOpen((was) => !was)}
            >
              <span className="eg-burger-bars" aria-hidden="true">
                <i />
                <i />
                <i />
              </span>
            </button>
          </div>
        </div>

      {/* The drawer. A real panel over a scrim, not the desktop row squeezed
          into a phone: at 390px the four items and the action need 44px touch
          targets, which no single row can hold. */}
      <div className="eg-drawer-wrap" id={drawerId} hidden={!open}>
        <button
          type="button"
          className="eg-drawer-scrim"
          tabIndex={-1}
          aria-hidden="true"
          onClick={() => setOpen(false)}
        />
        <div
          className="eg-drawer"
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
          tabIndex={-1}
          ref={drawerRef}
          onKeyDown={trapTab}
        >
          <div className="eg-drawer-top">
            <img
              className="eg-mark-img"
              src={P3_LOGO_WHITE}
              alt="P3"
              width={LOGO_W}
              height={LOGO_H}
            />
            <button
              type="button"
              className="eg-drawer-close"
              onClick={() => {
                setOpen(false);
                toggleRef.current?.focus();
              }}
            >
              Close
            </button>
          </div>
          <nav className="eg-drawer-nav" aria-label="Gateway">
            {GATEWAY_NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                aria-current={
                  item.page && item.page === current ? "page" : undefined
                }
                onClick={() => setOpen(false)}
              >
                {item.label}
              </a>
            ))}
          </nav>
          <a
            className="eg-nav-cta eg-drawer-cta"
            href="/checkout"
            onClick={() => setOpen(false)}
          >
            {CTA_LABEL}
          </a>
          <p className="eg-drawer-foot">
            {LEGAL.contactEmail}
            <br />
            501(c)(3) nonprofit · EIN {LEGAL.ein}
          </p>
        </div>
      </div>
    </header>
  );
}

/* ==========================================================================
   Footer

   TRIMMED HARD, September 8 2026. Thomas: "Double footer is quite heavy on
   text." It was: four columns of links, a 16 word brand line, a supplier
   block repeating an address, then a SECOND deck under a rule carrying the
   incorporation line and a 30 word paragraph about permanent document URLs.
   Two footers stacked, and about 90 words of it, under a page that had
   already said everything twice.

   WHAT SURVIVES, AND WHY EACH ONE EARNED ITS PLACE.
     · the lockup, because a page ends on the brand;
     · seven links in one row: the three destinations and the four documents
       a procurement office actually opens;
     · one legal line: entity, status, incorporation, EIN, and the contact
       address. That is what a requisition needs and it is the reason a
       procurement reader scrolls this far.
   Everything else moved or was already elsewhere. The permanent-URL promise
   is in the Terms themselves and on /trust, which is where somebody who
   cares about it is reading.
   ========================================================================== */

export function GatewayFooter() {
  return (
    <footer className="eg-footer">
      <div className="eg-shell">
        <div className="eg-footer-top">
          {/* Two elements, one per ground. The white lockup on the maroon
              footer; the colour lockup for the print sheet, where a white
              mark would be an empty rectangle. Filtering one asset into the
              other was tried and it destroys the artwork: brightness(0)
              invert(1) turns the mark's overlapping circles and the
              knocked-out "P3" into a solid white blob. */}
          <div className="eg-footer-brand">
            <img
              className="eg-footer-mark"
              src={P3_LOGO_WHITE}
              alt="P3"
              width={LOGO_W}
              height={LOGO_H}
            />
            <img
              className="eg-footer-mark is-print"
              src={P3_LOGO_COLOR}
              alt=""
              aria-hidden="true"
              width={LOGO_W}
              height={LOGO_H}
            />
          </div>

          {/* The navigation, and only that (Sep 19 2026, Thomas). The Terms,
              the Addendum and the W-9 left this row: /security lists all three
              with their statuses, which is where a reviewer looks for them,
              and the Terms are linked again at checkout where they are
              accepted. Privacy is the exception: nothing else on this host
              links it and checkout collects personal data, so it moves to the
              legal line below rather than off the page. */}
          <nav className="eg-footer-links" aria-label="Footer">
            <a href="/overview">Overview</a>
            <a href="/pricing">Pricing</a>
            <a href="/security">Security</a>
            <a href="/checkout">Register</a>
            <a href={LOGIN_PATH}>Login</a>
          </nav>
        </div>

        <div className="eg-footer-rule" />

        <p className="eg-footer-legal">
          {LEGAL.entity}, a {LEGAL.status} incorporated in{" "}
          {LEGAL.incorporation}. EIN {LEGAL.ein}.{" "}
          <a className="eg-footer-inline" href={`mailto:${LEGAL.contactEmail}`}>
            {LEGAL.contactEmail}
          </a>
          {" · "}
          <a className="eg-footer-inline" href={LEGAL.privacyUrl}>
            Privacy
          </a>
        </p>
      </div>
    </footer>
  );
}

/* ==========================================================================
   The frame
   ========================================================================== */

export interface GatewayShellProps {
  current: GatewayPage;
  /** Off when a host renders its own chrome, for instance a storybook page. */
  chrome?: boolean;
  /**
   * True when the page's first section is a GatewayHero, so the header goes
   * transparent and lets the hero's gradient run behind it. Optional and
   * defaulted, so no existing caller changes.
   */
  masthead?: boolean;
  /** The skip link's target and the main element's id. */
  mainId: string;
  /** What the skip link says it skips to. */
  skipLabel: string;
  /**
   * Off on the sign-in (Thomas, Sep 10 2026): the screen is one viewport
   * with nothing to read past, and the member app's sign-in has no footer
   * either. Every reading page keeps it.
   */
  footer?: boolean;
  className?: string;
  children: ReactNode;
}

export function GatewayShell({
  current,
  chrome = true,
  masthead = true,
  mainId,
  skipLabel,
  footer = true,
  className,
  children,
}: GatewayShellProps) {
  return (
    <div
      data-surface="enterprise"
      data-masthead={masthead ? "true" : undefined}
      className={cn("eg-page", className)}
    >
      <a className="e-skip" href={`#${mainId}`}>
        {skipLabel}
      </a>
      {chrome ? (
        <GatewayHeader current={current} tone={masthead ? "masthead" : "solid"} />
      ) : null}
      <main id={mainId} className="eg-shell">
        {children}
      </main>
      {footer ? <GatewayFooter /> : null}
    </div>
  );
}

/* ==========================================================================
   The selected tick

   Rendered inside a radio's mark on the checkout's path chooser. It is a
   drawn check rather than a text glyph so it inherits currentColor and prints
   at any size without a font dependency.
   ========================================================================== */

export function SelectedMark() {
  return (
    <svg
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2.5 6.2 4.8 8.6 9.5 3.4" />
    </svg>
  );
}
